import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
// import { Country, State, City } from "country-state-city"; // Dependency removed to save size
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "../common/NotificationContext";
import { db } from "@lib/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { storage } from "@lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export const InputFieldRace = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  eventId,
  categoryName,
  updateUserData,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });
  const [participantProvinces, setParticipantProvinces] = useState([
    [],
    [],
    [],
  ]);
  const [participantCities, setParticipantCities] = useState([[], [], []]);
  const [participantProvinceIds, setParticipantProvinceIds] = useState([
    {},
    {},
    {},
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
  const [genderError, setGenderError] = useState("");

  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const sizeChartUrl = "/jersey/sizechart.jpg";

  const cache = useMemo(() => new Map(), []);
  const isGroupRegistration =
    (eventId === "JOTR" || eventId === "JOTR2026") && categoryName?.includes("Group");
  const isUltraCategory = !categoryName?.toLowerCase().includes("fun run");
  const isRelayCategory = categoryName?.toLowerCase().includes("relay");

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Fetch data from Firestore on component mount (only once)
  useEffect(() => {
    if (!isGroupRegistration || isDraftLoaded) return;

    const fetchRegistrationData = async () => {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId || !eventId) {
        console.error(
          "[DEBUG] Missing userId or eventId for fetching registration data"
        );
        showNotification(
          "error",
          "Informasi pengguna atau event tidak lengkap."
        );
        return;
      }

      try {
        const docRef = doc(db, "registrations", `${userId}_${eventId}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(
            "[DEBUG] Fetched registration data from Firestore:",
            data
          );

          const defaultParticipant = {
            namaDepan: "",
            namaBelakang: "",
            bibName: "",
            telepon: "",
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            sizeJersey: "",
            gender: "",
            negara: "Indonesia",
            provinsi: "",
            kota: "",
            alamat: "",
            kontakDarurat: "",
            namaKontakDarurat: "",
            hubunganKontakDarurat: "",
            performanceIndex: "",
            raceResultLink: "",
          };

          const participants = Array(3)
            .fill()
            .map((_, index) => ({
              ...defaultParticipant,
              ...(data.participants && data.participants[index]
                ? data.participants[index]
                : {}),
            }));

          handleChange({
            target: {
              name: "participants",
              value: participants,
            },
          });

          if (data.teamName) {
            handleChange({
              target: {
                name: "teamName",
                value: data.teamName,
              },
            });
          }

          console.log("[DEBUG] Applied registration data to formData:", {
            participants,
            teamName: data.teamName,
          });
        } else {
          console.log(
            "[DEBUG] No registration data found in Firestore, using default formData."
          );
        }
      } catch (err) {
        console.error("[ERROR] Error fetching registration data:", err);
        showNotification("error", "Gagal memuat data registrasi dari server.");
      } finally {
        setIsDraftLoaded(true);
      }
    };

    fetchRegistrationData();
  }, [
    eventId,
    isGroupRegistration,
    handleChange,
    showNotification,
    isDraftLoaded,
  ]);

  const fetchWithCache = async (
    url,
    cacheKey,
    type,
    participantIndex = null
  ) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        setErrors((prev) => ({
          ...prev,
          [participantIndex !== null ? `${type}_${participantIndex}` : type]:
            "Gagal memuat data lokasi. Silakan coba lagi nanti.",
        }));
        showNotification(
          "error",
          "Gagal memuat data lokasi. Silakan coba lagi nanti."
        );
        return null;
      }
    } catch (err) {
      console.error(`[ERROR] Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [participantIndex !== null ? `${type}_${participantIndex}` : type]:
          "Gagal memuat data lokasi. Silakan coba lagi nanti.",
      }));
      showNotification(
        "error",
        "Gagal memuat data lokasi. Silakan coba lagi nanti."
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara && !isGroupRegistration) {
      setProvinces([]);
      setCities([]);

      if (rawCountries.length > 0) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === formData.negara
        );
        if (!selectedCountry) return;
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}`,
          "provinces"
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setProvinces(provinceList);
            setProvinceIds(idMap);
          }
        });
      }
    }
  }, [formData.negara, rawCountries, isGroupRegistration]);

  useEffect(() => {
    if (
      formData.provinsi &&
      !isGroupRegistration
    ) {
      setCities([]);

      if (provinceIds[formData.provinsi]) {
        const provinceId = provinceIds[formData.provinsi];
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${provinceId}?lang=en`,
          `cities_${provinceId}`,
          "cities"
        ).then((data) => {
          if (data?.geonames) {
            const cityList = data.geonames.map((city) => ({
              value: city.name,
              label: city.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setCities(cityList);
          }
        });
      }
    }
  }, [formData.provinsi, formData.negara, provinceIds, isGroupRegistration]);

  useEffect(() => {
    if (isGroupRegistration) {
      const participant = formData.participants[currentParticipantIndex];
      if (participant?.negara) {
        
        if (rawCountries.length > 0) {
            const selectedCountry = rawCountries.find(
            (c) => c.countryName === participant.negara
            );
            if (!selectedCountry) return;
    
            fetchWithCache(
            `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
            `provinces_${selectedCountry.geonameId}_${currentParticipantIndex}`,
            "provinces",
            currentParticipantIndex
            ).then((data) => {
            if (data?.geonames) {
                const provinceList = data.geonames.map((prov) => ({
                value: prov.name,
                label: prov.name,
                })).sort((a, b) => a.label.localeCompare(b.label));
                const idMap = {};
                data.geonames.forEach((prov) => {
                idMap[prov.name] = prov.geonameId;
                });
                setParticipantProvinces((prev) => {
                const newProvinces = [...prev];
                newProvinces[currentParticipantIndex] = provinceList;
                return newProvinces;
                });
                setParticipantProvinceIds((prev) => {
                const newIds = [...prev];
                newIds[currentParticipantIndex] = idMap;
                return newIds;
                });
    
                if (participant.provinsi && idMap[participant.provinsi]) {
                fetchWithCache(
                    `${GEO_API_BASE}/children/${idMap[participant.provinsi]}?lang=en`,
                    `cities_${
                    idMap[participant.provinsi]
                    }_${currentParticipantIndex}`,
                    "cities",
                    currentParticipantIndex
                ).then((data) => {
                    if (data?.geonames) {
                    const cityList = data.geonames.map((city) => ({
                        value: city.name,
                        label: city.name,
                    })).sort((a, b) => a.label.localeCompare(b.label));
                    setParticipantCities((prev) => {
                        const newCities = [...prev];
                        newCities[currentParticipantIndex] = cityList;
                        return newCities;
                    });
                    }
                });
                }
            }
            });
        }
      }
    }
  }, [
    formData.participants,
    rawCountries,
    isGroupRegistration,
    currentParticipantIndex,
    cache
  ]);

  const validateField = (
    name,
    value,
    participantIndex = null,
    negara = "Indonesia"
  ) => {
    const label = humanize(name);

    // Validate required fields - but make provinsi and kota optional if no options available
    if (value === undefined || value === null || value === "") {
      // For provinsi, check if there are provinces available for the selected country
      if (name === "provinsi") {
        const provinceList = participantIndex !== null ? participantProvinces[participantIndex] : provinces;
        if (provinceList.length === 0) {
          return ""; // No error if no provinces available
        }
      }
      // For kota, check if there are cities available for the selected province
      if (name === "kota") {
        const cityList = participantIndex !== null ? participantCities[participantIndex] : cities;
        if (cityList.length === 0) {
          return ""; // No error if no cities available
        }
      }
      return `${label} wajib diisi`;
    }

    if (name === "teamName" && value) {
      if (!/^[A-Za-z0-9\s]{3,50}$/.test(value)) {
        return `${label} harus 3-50 karakter, alfanumerik dengan spasi, misalnya: "Tim Lari Cepat"`;
      }
    }
    if (name === "namaDepan" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Budi"`;
      }
    }
    if (name === "namaBelakang" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Santoso"`;
      }
    }
    if (name === "bibName" && value) {
      if (!/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
        return `${label} harus 1-20 karakter alfanumerik dan spasi, misalnya: "BUDI123"`;
      }
    }
    if (name === "telepon" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
      }
    }
    if (name === "nomorIdentitas" && value) {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) harus 16 digit untuk peserta dari Indonesia, misalnya: "1234567890123456"`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Paspor/Identitas) harus 5-20 karakter alfanumerik untuk peserta asing, misalnya: "AB123456"`;
        }
      }
    }
    if (name === "tanggalLahir" && value) {
      const date = new Date(value);
      const eventDate = new Date("2026-07-24");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 17,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} harus di masa lalu sebelum 24/07/2026`;
      }
      if (date > minAgeDate) {
        return `Peserta harus berusia minimal 17 tahun pada 24/07/2026`;
      }
    }
    if (name === "golonganDarah" && value) {
      if (!["A", "B", "AB", "O"].includes(value)) {
        return `${label} harus A, B, AB, atau O`;
      }
    }
    if (name === "sizeJersey" && value) {
      if (!["XS", "S", "M", "L", "XL", "XXL"].includes(value)) {
        return `${label} harus XS, S, M, L, XL, atau XXL`;
      }
    }
    if (name === "gender" && value) {
      if (!["Laki-laki", "Perempuan"].includes(value)) {
        return `${label} harus Laki-laki atau Perempuan`;
      }
    }
    if (name === "negara" && value) {
      if (countries.length > 0 && !countries.some((c) => c.value === value)) {
        return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Indonesia"`;
      }
    }
    if (name === "provinsi" && value) {
      const provinceList =
        participantIndex !== null
          ? participantProvinces[participantIndex]
          : provinces;
      if (
        provinceList.length > 0 &&
        !provinceList.some((p) => p.value === value)
      ) {
        return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Jawa Barat"`;
      }
    }
    if (name === "kota" && value) {
      const cityList =
        participantIndex !== null
          ? participantCities[participantIndex]
          : cities;
      if (cityList.length > 0 && !cityList.some((c) => c.value === value)) {
        return `${label} harus dipilih dari daftar yang tersedia, misalnya: "Bandung"`;
      }
    }
    if (name === "alamat" && value) {
      if (!/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
        return `${label} harus 5-100 karakter, boleh huruf, angka, spasi, koma, titik, tanda hubung, garis miring, pagar, atau tanda kurung, misalnya: "Jalan Merdeka No. 123"`;
      }
    }
    if (name === "kontakDarurat" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+", misalnya: "+6281234567890"`;
      }
    }
    if (name === "namaKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Ani"`;
      }
    }
    if (name === "hubunganKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter, misalnya: "Istri"`;
      }
    }
    if (name === "performanceIndex" && value) {
      if (!/^\d{3}$/.test(value)) {
        return `${label} harus berupa 3 digit angka, misalnya: "460"`;
      }
    }
    if (name === "raceResultLink" && value) {
      return "";
    }
    if (name === "riwayatMedis") {
       // Optional field for Medical History ("Riwayat Kesehatan")
       return "";
    }
    if (name === "certificateUrl") {
      // Optional field
      return "";
    }
    return "";
  };

  const validateIndividual = () => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      ...(isUltraCategory ? ["raceResultLink"] : []),
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        formData[field],
        null,
        formData.negara
      );
      if (error && touched[field]) {
        errors[field] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateParticipant = (participantData, index) => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      "raceResultLink",
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        participantData[field],
        index,
        participantData.negara
      );
      if (error) {
        errors[`${field}_${index}`] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateAllParticipants = async () => {
    let allValid = true;
    let allErrors = {};
    const bibNames = new Set();

    setTouched((prev) => {
      const newTouched = { ...prev };
      formData.participants.forEach((participant, index) => {
        Object.keys(participant).forEach((field) => {
          newTouched[`${field}_${index}`] = true;
        });
      });
      return newTouched;
    });

    for (let index = 0; index < formData.participants.length; index++) {
      const participant = formData.participants[index];
      const { isValid, errors } = validateParticipant(participant, index);
      if (!isValid) {
        allValid = false;
        allErrors = { ...allErrors, ...errors };
      }
      if (participant.bibName) {
        if (bibNames.has(participant.bibName)) {
          allErrors[`bibName_${index}`] = `BIB Name sudah digunakan`;
          allValid = false;
        } else {
          bibNames.add(participant.bibName);
        }
      }
    }

    const allMale = formData.participants.every(
      (p) => p.gender === "Laki-laki"
    );
    if (allMale) {
      setGenderError(
        "Tim tidak boleh terdiri dari pria semua. Minimal satu peserta harus perempuan."
      );
      allValid = false;
    } else {
      setGenderError("");
    }

    setErrors(allErrors);
    return allValid;
  };

  const handleFieldChange = (e, participantIndex = null) => {
    const { name, value } = e.target;
    handleChange(e, participantIndex);
    setTouched((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: true,
    }));
    const error = validateField(
      name,
      value,
      participantIndex,
      participantIndex !== null
        ? formData.participants[participantIndex]?.negara
        : formData.negara
    );
    setErrors((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: error,
    }));
  };

  const saveToFirestore = async (showSuccessNotification = false) => {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId || !eventId) {
      console.error("[ERROR] Missing userId or eventId for Firestore save");
      showNotification(
        "error",
        "Gagal menyimpan data: Informasi pengguna atau event tidak lengkap."
      );
      return false;
    }

    try {
      const docRef = doc(db, "registrations", `${userId}_${eventId}`);
      const draftData = {
        participants: formData.participants,
        teamName: formData.teamName || "",
        eventId,
        userId,
        status: "draft",
        updatedAt: serverTimestamp(),
      };
      await setDoc(docRef, draftData);
      if (showSuccessNotification) {
        showNotification(
          "success",
          "Data semua peserta telah disimpan sementara!"
        );
      }
      return true;
    } catch (err) {
      console.error("[ERROR] Error saving to Firestore:", err);
      showNotification("error", "Gagal menyimpan data peserta ke server.");
      return false;
    }
  };

  const handleNextParticipant = async () => {
    const currentParticipant = formData.participants[currentParticipantIndex];
    if (!currentParticipant) {
      console.error(
        "[ERROR] Current participant is undefined at index:",
        currentParticipantIndex
      );
      showNotification(
        "error",
        `Data peserta ${currentParticipantIndex + 1} tidak ditemukan.`
      );
      return;
    }
    const { isValid, errors: newErrors } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );

    setTouched((prev) => {
      const newTouched = { ...prev };
      Object.keys(currentParticipant).forEach((field) => {
        newTouched[`${field}_${currentParticipantIndex}`] = true;
      });
      return newTouched;
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    if (!isValid) {
      const firstErrorKey = Object.keys(newErrors).find(
        (key) => newErrors[key]
      );
      const firstError = firstErrorKey
        ? newErrors[firstErrorKey]
        : "Harap lengkapi data peserta dengan benar.";
      showNotification("error", firstError);
      return;
    }

    const success = await saveToFirestore();
    if (!success) return;

    if (currentParticipantIndex === 0 && updateUserData) {
      const updatedFields = {
        namaDepan: currentParticipant.namaDepan,
        namaBelakang: currentParticipant.namaBelakang,
        namaLengkap:
          `${currentParticipant.namaDepan} ${currentParticipant.namaBelakang}`.trim(),
        telepon: currentParticipant.telepon,
        nomorIdentitas: currentParticipant.nomorIdentitas,
        tanggalLahir: currentParticipant.tanggalLahir,
        gender: currentParticipant.gender,
        negara: currentParticipant.negara,
        provinsi: currentParticipant.provinsi,
        kota: currentParticipant.kota,
        alamat: currentParticipant.alamat,
        kontakDarurat: currentParticipant.kontakDarurat,
        namaKontakDarurat: currentParticipant.namaKontakDarurat,
        hubunganKontakDarurat: currentParticipant.hubunganKontakDarurat,
        bibName: currentParticipant.bibName,
        golonganDarah: currentParticipant.golonganDarah,
        sizeJersey: currentParticipant.sizeJersey,
        performanceIndex: currentParticipant.performanceIndex,
        raceResultLink: currentParticipant.raceResultLink,
        certificateUrl: currentParticipant.certificateUrl,
      };

      try {
        await updateUserData(updatedFields);
      } catch (err) {
        console.error("[ERROR] Error updating user data:", err);
        showNotification("error", "Gagal memperbarui data pengguna.");
      }
    }

    if (currentParticipantIndex < 2) {
      setCurrentParticipantIndex(currentParticipantIndex + 1);
      setGenderError("");
    } else {
      const allValid = await validateAllParticipants();
      if (allValid) {
        await saveToFirestore(true);
        showNotification(
          "success",
          "Data semua peserta telah disimpan sementara! Silakan lanjutkan ke submit."
        );
      } else {
        const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
        const firstError = firstErrorKey
          ? errors[firstErrorKey]
          : genderError ||
            "Ada kesalahan dalam data peserta. Silakan periksa kembali.";
        showNotification("error", firstError);
      }
    }
  };

  const handlePreviousParticipant = () => {
    if (currentParticipantIndex > 0) {
      setCurrentParticipantIndex(currentParticipantIndex - 1);
      setGenderError("");
    }
  };

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(false);
  };

  const handleCertificateUpload = async (e, participantIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "Ukuran file maksimal 5MB");
      return;
    }

    setUploadingCertificate(true);
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      const timestamp = Date.now();
      const fileName = `cert_${userId}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `qualifications/${userId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange({
        target: {
          name: "certificateUrl",
          value: url
        }
      }, participantIndex);
      
      showNotification("success", "Sertifikat berhasil diunggah!");
    } catch (err) {
      console.error("Upload error:", err);
      showNotification("error", "Gagal mengunggah sertifikat. Silakan coba lagi.");
    } finally {
      setUploadingCertificate(false);
    }
  };

  const isNextDisabled = () => {
    if (
      !formData.participants ||
      !formData.participants[currentParticipantIndex]
    ) {
      return true;
    }
    const currentParticipant = formData.participants[currentParticipantIndex];
    const { isValid } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );
    return !isValid;
  };

  const getError = (field, participantIndex = null) => {
    const localError =
      participantIndex !== null
        ? errors[`${field}_${participantIndex}`]
        : errors[field];
    const formError =
      participantIndex !== null
        ? formData.errors?.[`${field}_${participantIndex}`]
        : formData.errors?.[field];
    return touched[field] ||
      (participantIndex !== null && touched[`${field}_${participantIndex}`])
      ? localError || formError
      : formError;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}

      {isGroupRegistration &&
      formData.participants &&
      formData.participants.length === 3 ? (
        <>
          <InputField
            label="Nama Tim"
            name="teamName"
            required
            value={formData.teamName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("teamName")}
            labelColor={labelColor}
          />
          <div className="col-span-full">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Data Peserta Tim (Wajib 3 Orang, Minimal 1 Perempuan)
            </h3>
            <div className="flex justify-center mb-4">
              {[1, 2, 3].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === currentParticipantIndex
                        ? "bg-red-600 text-white"
                        : "bg-slate-200 text-slate-800"
                    }`}
                  >
                    {step}
                  </div>
                  {index < 2 && (
                    <div className="w-8 h-1 bg-slate-200 mx-2"></div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {currentParticipantIndex === 0
                ? "Isi data diri Anda"
                : currentParticipantIndex === 1
                ? "Isi data anggota pertama"
                : "Isi data anggota kedua"}
            </p>
            {genderError && <p className="text-red-500 mb-4">{genderError}</p>}
            <motion.div
              key={currentParticipantIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className=""
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Nama Depan"
                  name="namaDepan"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.namaDepan ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaDepan", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Nama Belakang"
                  name="namaBelakang"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaBelakang || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaBelakang", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="BIB Name"
                  name="bibName"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.bibName ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("bibName", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Nomor Identitas"
                  name="nomorIdentitas"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.nomorIdentitas || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("nomorIdentitas", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder={
                    formData.participants[currentParticipantIndex]?.negara ===
                    "Indonesia"
                      ? "Masukkan 16 digit KTP"
                      : "Masukkan nomor Paspor/Identitas"
                  }
                />
                <InputField
                  label="Tanggal Lahir"
                  name="tanggalLahir"
                  type="date"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.tanggalLahir || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("tanggalLahir", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Golongan Darah"
                  name="golonganDarah"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.golonganDarah || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Pilih Golongan Darah" },
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "AB", label: "AB" },
                    { value: "O", label: "O" },
                  ]}
                  error={getError("golonganDarah", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <div className="relative">
                  <InputField
                    label="Size Jersey"
                    name="sizeJersey"
                    type="select"
                    required
                    value={
                      formData.participants[currentParticipantIndex]
                        ?.sizeJersey || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(e, currentParticipantIndex)
                    }
                    disabled={isFormDisabled}
                    options={[
                      { value: "", label: "Pilih Size Jersey" },
                      { value: "XS", label: "XS" },
                      { value: "S", label: "S" },
                      { value: "M", label: "M" },
                      { value: "L", label: "L" },
                      { value: "XL", label: "XL" },
                      { value: "XXL", label: "XXL" },
                    ]}
                    error={getError("sizeJersey", currentParticipantIndex)}
                    labelColor={labelColor}
                  />
                  <button
                    type="button"
                    onClick={openModal}
                    className="absolute top-0 right-0 mt-2 mr-2 text-red-600 hover:text-lime-800 text-sm underline"
                    disabled={isFormDisabled}
                  >
                    Lihat Size Chart
                  </button>
                </div>
                <InputField
                  label="Gender"
                  name="gender"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.gender || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Pilih Gender" },
                    { value: "Laki-laki", label: "Laki-laki" },
                    { value: "Perempuan", label: "Perempuan" },
                  ]}
                  error={getError("gender", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Nomor Telepon/WhatsApp"
                  name="telepon"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.telepon ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("telepon", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Negara"
                  name="negara"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.negara ||
                    "Indonesia"
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled || loading.countries}
                  options={[{ value: "", label: "Pilih Negara" }, ...countries]}
                  error={getError("negara", currentParticipantIndex)}
                  aria-busy={loading.countries}
                  labelColor={labelColor}
                />
                <InputField
                  label="Provinsi"
                  name="provinsi"
                  type="select"
                  required={participantProvinces[currentParticipantIndex]?.length > 0}
                  value={
                    formData.participants[currentParticipantIndex]?.provinsi ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.negara ||
                    loading.provinces
                  }
                  options={[
                    { value: "", label: participantProvinces[currentParticipantIndex]?.length > 0 ? "Pilih Provinsi" : "Tidak ada provinsi tersedia" },
                    ...participantProvinces[currentParticipantIndex],
                  ]}
                  error={getError("provinsi", currentParticipantIndex)}
                  aria-busy={loading.provinces}
                  labelColor={labelColor}
                />
                <InputField
                  label="Kota"
                  name="kota"
                  type="select"
                  required={participantCities[currentParticipantIndex]?.length > 0}
                  value={
                    formData.participants[currentParticipantIndex]?.kota || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.provinsi ||
                    loading.cities
                  }
                  options={[
                    { value: "", label: participantCities[currentParticipantIndex]?.length > 0 ? "Pilih Kota" : "Tidak ada kota tersedia" },
                    ...participantCities[currentParticipantIndex],
                  ]}
                  error={getError("kota", currentParticipantIndex)}
                  aria-busy={loading.cities}
                  labelColor={labelColor}
                />
                <InputField
                  label="Alamat"
                  name="alamat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.alamat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("alamat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Nomor Kontak Darurat"
                  name="kontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.kontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("kontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Nama Kontak Darurat"
                  name="namaKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaKontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Hubungan Dengan Kontak Darurat"
                  name="hubunganKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.hubunganKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError(
                    "hubunganKontakDarurat",
                    currentParticipantIndex
                  )}
                  labelColor={labelColor}
                />
                <InputField
                  label="ITRA Performance Index"
                  name="performanceIndex"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.performanceIndex || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("performanceIndex", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Contoh: 460"
                />
                <InputField
                  label="Activity STRAVA atau Race Result"
                  name="raceResultLink"
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.raceResultLink || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  required
                  error={getError("raceResultLink", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Contoh: https://www.strava.com/activities/12523135176"
                />
              </div>
            </motion.div>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={handlePreviousParticipant}
                className={`py-2 px-4 rounded-xl ${
                  currentParticipantIndex === 0
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-lime-700 text-white"
                }`}
                disabled={currentParticipantIndex === 0 || isFormDisabled}
              >
                Sebelumnya
              </button>
              <button
                type="button"
                onClick={handleNextParticipant}
                className={`py-2 px-4 rounded-xl ${
                  isNextDisabled()
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-lime-700 text-white"
                }`}
                disabled={isNextDisabled() || isFormDisabled}
              >
                {currentParticipantIndex < 2 ? "Berikutnya" : "Selesai"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <InputField
            label="Nama Depan"
            name="namaDepan"
            value={formData.namaDepan || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaDepan")}
            labelColor={labelColor}
          />
          <InputField
            label="Nama Belakang"
            name="namaBelakang"
            value={formData.namaBelakang || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaBelakang")}
            labelColor={labelColor}
          />
          <InputField
            label="BIB Name"
            name="bibName"
            value={formData.bibName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("bibName")}
            labelColor={labelColor}
          />
          <InputField
            label="Nomor Identitas"
            name="nomorIdentitas"
            value={formData.nomorIdentitas || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("nomorIdentitas")}
            labelColor={labelColor}
            placeholder={
              formData.negara === "Indonesia"
                ? "Masukkan 16 digit KTP"
                : "Masukkan nomor Paspor/Identitas"
            }
          />
          <InputField
            label="Tanggal Lahir"
            name="tanggalLahir"
            type="date"
            value={formData.tanggalLahir || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("tanggalLahir")}
            labelColor={labelColor}
          />
          <InputField
            label="Golongan Darah"
            name="golonganDarah"
            type="select"
            value={formData.golonganDarah || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            options={[
              { value: "", label: "Pilih Golongan Darah" },
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "AB", label: "AB" },
              { value: "O", label: "O" },
            ]}
            error={getError("golonganDarah")}
            labelColor={labelColor}
          />
          <div className="relative">
            <InputField
              label="Size Jersey"
              name="sizeJersey"
              type="select"
              value={formData.sizeJersey || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              options={[
                { value: "", label: "Pilih Size Jersey" },
                { value: "XS", label: "XS" },
                { value: "S", label: "S" },
                { value: "M", label: "M" },
                { value: "L", label: "L" },
                { value: "XL", label: "XL" },
                { value: "XXL", label: "XXL" },
              ]}
              error={getError("sizeJersey")}
              labelColor={labelColor}
            />
            <button
              type="button"
              onClick={openModal}
              className="absolute top-0 right-0 mt-2 mr-2 text-red-600 hover:text-lime-800 text-sm underline"
              disabled={isFormDisabled}
            >
              Lihat Size Chart
            </button>
          </div>
          <InputField
            label="Gender"
            name="gender"
            type="select"
            value={formData.gender || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("gender")}
            labelColor={labelColor}
            options={[
              { value: "", label: "Pilih Gender" },
              { value: "Laki-laki", label: "Laki-laki" },
              { value: "Perempuan", label: "Perempuan" },
            ]}
          />
          <InputField
            label="Nomor Telepon/WhatsApp"
            name="telepon"
            value={formData.telepon || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("telepon")}
            labelColor={labelColor}
          />
          <InputField
            label="Negara"
            name="negara"
            type="select"
            value={formData.negara || "Indonesia"}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || loading.countries}
            options={[{ value: "", label: "Pilih Negara" }, ...countries]}
            required
            error={getError("negara")}
            aria-busy={loading.countries}
            labelColor={labelColor}
          />
          <InputField
            label="Provinsi"
            name="provinsi"
            type="select"
            value={formData.provinsi || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.negara || loading.provinces}
            options={[{ value: "", label: provinces.length > 0 ? "Pilih Provinsi" : "Tidak ada provinsi tersedia" }, ...provinces]}
            required={provinces.length > 0}
            error={getError("provinsi")}
            aria-busy={loading.provinces}
            labelColor={labelColor}
          />
          <InputField
            label="Kota"
            name="kota"
            type="select"
            value={formData.kota || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.provinsi || loading.cities}
            options={[{ value: "", label: cities.length > 0 ? "Pilih Kota" : "Tidak ada kota tersedia" }, ...cities]}
            required={cities.length > 0}
            error={getError("kota")}
            aria-busy={loading.cities}
            labelColor={labelColor}
          />
          <InputField
            label="Alamat"
            name="alamat"
            value={formData.alamat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("alamat")}
            labelColor={labelColor}
          />
          <InputField
            label="Nomor Kontak Darurat"
            name="kontakDarurat"
            value={formData.kontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("kontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Nama Kontak Darurat"
            name="namaKontakDarurat"
            value={formData.namaKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaKontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Hubungan Dengan Kontak Darurat"
            name="hubunganKontakDarurat"
            value={formData.hubunganKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("hubunganKontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="ITRA Performance Index"
            name="performanceIndex"
            value={formData.performanceIndex || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("performanceIndex")}
            labelColor={labelColor}
            placeholder="Contoh: 460"
          />
          {isUltraCategory && (
            <InputField
              label="Activity STRAVA atau Race Result"
              name="raceResultLink"
              value={formData.raceResultLink || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              error={getError("raceResultLink")}
              labelColor={labelColor}
              placeholder="Contoh: https://www.strava.com/activities/12523135176"
            />
          )}

          {isUltraCategory && (
            <div className="space-y-1">
              <InputField
                label="Unggah Sertifikat (Maks 5MB, Opsional)"
                name="certificate"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleCertificateUpload(e)}
                disabled={isFormDisabled || uploadingCertificate}
                error={getError("certificateUrl")}
                labelColor={labelColor}
              />
              {formData.certificateUrl && (
                <div className="text-xs text-red-600 pl-1">
                  File terunggah: <a href={formData.certificateUrl} target="_blank" rel="noopener noreferrer" className="underline font-medium">Lihat Sertifikat</a>
                </div>
              )}
              {uploadingCertificate && <span className="text-xs text-slate-500 pl-1">Mengunggah...</span>}
            </div>
          )}

          <InputField
            label="Riwayat Kesehatan (Penyakit / Alergi)"
            name="riwayatMedis"
            value={formData.riwayatMedis || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("riwayatMedis")}
            labelColor={labelColor}
            placeholder="Contoh: Asma, alergi obat tertentu. Isi 'Tidak ada' jika tidak ada."
          />
        </>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={sizeChartUrl}
                alt="Size Chart"
                className="w-full h-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
